import { Database, FileText, FolderInput, LayoutTemplate, ShieldCheck, Users } from "lucide-react";
import { useTranslation } from "react-i18next";
import { NavLink } from "react-router";

import { HASURA_EDITOR_ROLES, useAuthorization, useIsActiveRolloAdmin } from "~/features/authz/authorization";
import { cn } from "~/lib/utils";

interface NavEntry {
  to: string;
  labelKey: string;
  requiresEditorSchema?: boolean;
}

interface NavGroup {
  labelKey: string;
  icon: React.ComponentType<{ className?: string }>;
  entries: NavEntry[];
}

interface NavSection {
  groups: NavGroup[];
  /** When set, the section is only rendered if `checkAccess({ allowedRoles })` passes. */
  requiresRoles?: ("admin" | "tenantAdmin")[];
}

// Static t() references so i18next-extract (removeUnusedKeys) keeps the dynamically-used labelKeys.
// t("ui.data") t("ui.import") t("ui.templates") t("ui.dm") t("model.tenant.navigatorLabel") t("feature.admin.organizationsAndTenants")
const dataSection: NavSection = {
  groups: [
    {
      labelKey: "ui.data",
      icon: Database,
      entries: [
        { to: "/entities", labelKey: "model.record.entity.modelNames", requiresEditorSchema: true },
        { to: "/relations", labelKey: "model.record.relation.modelNames", requiresEditorSchema: true },
        { to: "/compound-entities", labelKey: "model.compoundEntity.modelNames" },
        { to: "/evidences", labelKey: "model.evidence.modelNames" },
        { to: "/references", labelKey: "model.reference.modelNames", requiresEditorSchema: true },
        { to: "/compound-record-versions", labelKey: "model.compoundRecordVersion.modelNames" },
        { to: "/geo-items", labelKey: "model.geoItem.modelNames" },
      ],
    },
  ],
};

const importSection: NavSection = {
  groups: [
    {
      labelKey: "ui.import",
      icon: FolderInput,
      entries: [
        { to: "/data-import-projects", labelKey: "model.dataImportProject.modelNames" },
        { to: "/compound-record-composers", labelKey: "model.compoundRecordComposer.list" },
        { to: "/georeferencer", labelKey: "ui.georeferencer" },
        // Not yet migrated to v2 — link to the v1 (root) pages until the v2 versions exist.
        { to: "/change-requests", labelKey: "model.changeRequest.modelNames" },
        { to: "/dbpjkit-worker-jobs", labelKey: "model.dbpjkitWorkerJob.modelNames" },
      ],
    },
    {
      labelKey: "ui.templates",
      icon: LayoutTemplate,
      // Not yet migrated to v2 — link to the v1 (root) page.
      entries: [{ to: "/data-source-templates", labelKey: "model.dataSourceTemplate.modelNames" }],
    },
  ],
};

const adminSection: NavSection = {
  requiresRoles: ["admin"],
  groups: [
    {
      labelKey: "ui.dm",
      icon: ShieldCheck,
      entries: [
        { to: "/admin/categories", labelKey: "model.category.modelNames" },
        { to: "/admin/record-metadata-schema", labelKey: "model.recordMetadataSchema.modelNames" },
        { to: "/admin/relation-types", labelKey: "model.relationType.modelNames" },
      ],
    },
    {
      labelKey: "model.user.modelNames",
      icon: Users,
      entries: [
        { to: "/admin/users", labelKey: "model.user.modelNames" },
        { to: "/admin/groups", labelKey: "model.group.modelNames" },
        { to: "/admin/products", labelKey: "model.product.modelNames" },
      ],
    },
  ],
};

// Organizations and Tenants share this group behind the same gate (admin + active Rollo).
const rolloAdminGroup: NavGroup = {
  labelKey: "feature.admin.organizationsAndTenants",
  icon: FileText,
  entries: [
    { to: "/admin/organizations", labelKey: "model.organization.modelNames" },
    { to: "/admin/tenants", labelKey: "model.tenant.modelNames" },
  ],
};

export function Sidebar() {
  const { t } = useTranslation();
  const { checkAccess } = useAuthorization();
  const isActiveRolloAdmin = useIsActiveRolloAdmin();

  const isAdmin = checkAccess({ allowedRoles: ["admin"] });

  return (
    <aside className="sticky top-0 flex h-screen w-[var(--sidenav-w)] flex-col gap-1 overflow-y-auto border-r border-border-default bg-white p-4">
      <div className="mb-4 flex items-center px-3 py-3">
        {/* Brand name — never translated. */}
        {/* eslint-disable-next-line i18next/no-literal-string */}
        <span className="text-dewey-brand text-primary-700">Dewey</span>
      </div>

      <SectionRender section={dataSection} t={t} />
      <SectionRender section={importSection} t={t} />

      {isAdmin && (
        <>
          <hr className="my-3 border-0 border-t border-border-default" />
          {isActiveRolloAdmin && <GroupRender group={rolloAdminGroup} t={t} />}
          <SectionRender section={adminSection} t={t} />
        </>
      )}

      <div className="mt-auto p-3 text-[11px] leading-[1.6] text-fg-3">
        <div className="mb-1 font-semibold text-fg-2">{t("feature.v2.contribution.title")}</div>
        {t("feature.v2.contribution.body", { entered: 0, published: 0 })}
      </div>
    </aside>
  );
}

function SectionRender({ section, t }: { section: NavSection; t: (k: string) => string }) {
  return (
    <>
      {section.groups.map((group) => (
        <GroupRender key={group.labelKey} group={group} t={t} />
      ))}
    </>
  );
}

function GroupRender({ group, t }: { group: NavGroup; t: (k: string) => string }) {
  const Icon = group.icon;
  const { checkAccess } = useAuthorization();
  const hasEditorSchema = checkAccess({ allowedRoles: HASURA_EDITOR_ROLES });
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-1.5 px-3 pb-1 pt-2 text-[11px] font-bold uppercase tracking-[0.06em] text-fg-3">
        <Icon className="h-3.5 w-3.5" />
        <span>{t(group.labelKey)}</span>
      </div>
      {group.entries
        .filter((entry) => !entry.requiresEditorSchema || hasEditorSchema)
        .map(({ to, labelKey }) => (
          <NavLink
            key={to}
            to={to}
            className={({ isActive }) =>
              cn(
                "flex items-center gap-2.5 rounded-[var(--radius-sm)] px-3 py-1.5 text-[13px] font-medium no-underline capitalize transition-colors",
                isActive
                  ? "bg-primary-50 font-semibold text-primary-700"
                  : "text-fg-2 hover:bg-bg-subtle hover:text-fg-1",
              )
            }
          >
            <span>{t(labelKey)}</span>
          </NavLink>
        ))}
    </div>
  );
}
